import { useQuery } from '@apollo/client';
import { SupportPolicy } from '@provision/entity-types';
import { QueryHookOptions } from '@provision/graphql-client';
import { useMemo } from 'react';
import { GET_SUPPORT_POLICY_QUERY } from 'src/graphql/entities/support-policy/support-policy.operations';
import {
  NormalizedSupportPolicy,
  normalizeSupportPolicyRecord,
} from 'src/graphql/entities/support-policy/support-policy.utils';
import { ConditionInput, QueryVariables, Records, SortingInput } from 'src/graphql/types';
import { useLoadMore } from 'src/graphql/utils';
import { useNotifications } from 'src/utils/hooks';

export type GetSupportPolicyQuery = {
  getSupportPolicy: Records<SupportPolicy>;
};

export type GetSupportPolicyParams = {
  filters?: ConditionInput[];
  sorting?: SortingInput[];
  limit?: number;
  offset?: number;
};

export const useSupportPolicies = (
  params: GetSupportPolicyParams = {},
  options: QueryHookOptions<GetSupportPolicyQuery> = {},
) => {
  const { showErrorAlert } = useNotifications();

  const {
    data,
    loading: isLoading,
    fetchMore,
    variables,
  } = useQuery<GetSupportPolicyQuery, QueryVariables>(GET_SUPPORT_POLICY_QUERY, {
    ...options,
    variables: {
      params: {
        ...params,
        sorting: [
          { attribute: 'pinnedAt', desc: true },
          { attribute: 'createdAt', desc: true },
        ],
      },
    },
    context: {
      omitIsolationModeHeader: true,
    },
    skip: !params?.filters?.length,
    onError: (error) => {
      console.error(error);
      showErrorAlert('Failed to fetch support policies, please try again');
    },
  });

  const normalizedRecords = useMemo(
    () => (data?.getSupportPolicy?.records ?? []).map(normalizeSupportPolicyRecord),
    [data?.getSupportPolicy?.records],
  );

  const records = useMemo(() => sortSupportPolicies(normalizedRecords), [normalizedRecords]);

  const totalRecords = data?.getSupportPolicy?.metadata?.totalRecords ?? 0;

  const { loadMore, hasReachedEnd } = useLoadMore<GetSupportPolicyQuery>({
    offset: records?.length ?? 0,
    fetchMore,
    params: variables?.params!,
  });

  return {
    supportPolicies: records,
    isLoading,
    loadMore,
    totalRecords,
    hasMore: totalRecords ? records?.length < totalRecords : !hasReachedEnd,
  };
};

function sortSupportPolicies(policies: NormalizedSupportPolicy[]): NormalizedSupportPolicy[] {
  return policies.slice().sort((a, b) => {
    const aPinned = Boolean(a.pinnedAt);
    const bPinned = Boolean(b.pinnedAt);

    if (aPinned && !bPinned) return -1;
    if (!aPinned && bPinned) return 1;

    // Both are either pinned or unpinned — sort by createdAt descending
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });
}
